import type { ReactNode } from "react";

import { Button, Modal, Span } from "@virtual-brain-lib/frontend-design-system/ui";
import { useIsOpen } from "@virtual-brain-lib/frontend-design-system/utils";
import { truncateAtWordBoundary } from "@virtual-brain-lib/utils-text-processing";

import { Markdown } from "../../../markdown";

interface UserMessageBubbleProps {
	readonly children: ReactNode;
	readonly content: string;
	readonly charLimit?: number;
}

export default function UserMessageBubble({
	children,
	content,
	charLimit,
}: UserMessageBubbleProps) {
	const { isOpen, open, close } = useIsOpen();

	const plainTextLength = content.replace(/<[^>]*>/g, "").length;
	const shouldTruncate = charLimit !== undefined && plainTextLength > charLimit;
	const truncatedContent = shouldTruncate
		? `${truncateAtWordBoundary(content, charLimit)}…`
		: content;

	return (
		<>
			<Span
				isBlock
				variant="body-lg"
				className="w-full rounded-lg bg-primary px-8 py-3 text-on-primary"
			>
				{shouldTruncate ? (
					<>
						<div className="markdown-content w-full break-words">
							<Markdown>{truncatedContent}</Markdown>
						</div>
						<Button
							variant="ghost"
							size="xs"
							className="text-on-primary underline"
							classNames={{
								base: "h-auto min-h-0 px-0",
								text: "text-sm",
							}}
							onClick={open}
						>
							show more
						</Button>
					</>
				) : (
					children
				)}
			</Span>

			<Modal
				isOpen={isOpen}
				close={close}
				title="Full user message"
				size="lg"
				footer={undefined}
			>
				<div className="markdown-content">
					<Markdown>{content}</Markdown>
				</div>
			</Modal>
		</>
	);
}
